import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Button, Alert, Table } from 'react-bootstrap';
import Select from './common/Select';
import type { InputActionMeta } from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetCustomersIdLocations,
  useGetCustomersIdSlingLocationMappings,
  getSlingLocations,
  usePostSlingLocationMappings,
  usePutSlingLocationMappingsId,
  useDeleteSlingLocationMappingsId,
  getGetCustomersIdQueryKey,
  getGetSlingLocationsQueryKey,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { CustomerSlingLocationMap } from '../api/generated/model/customerSlingLocationMap';
import type { SlingLocation } from '../api/generated/model/slingLocation';

interface SlingLocationMappingsModalProps {
  show: boolean;
  onHide: () => void;
  customerId: string;
  customerName: string;
}

interface MappingRow {
  location: string | null;
  displayName: string;
  slingLocationSrcId: string;
  slingLocationId: number;
  mappingId?: string;
  hasChanges: boolean;
}

interface SlingLocationOption {
  value: string;
  label: string;
  isAlreadyMapped: boolean;
  slingLocation: SlingLocation;
}

const SlingLocationMappingsModal: React.FC<SlingLocationMappingsModalProps> = ({
  show,
  onHide,
  customerId,
  customerName,
}) => {
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmMapping, setConfirmMapping] = useState<{
    rowIndex: number;
    slingLocation: SlingLocation;
    existingCustomerName?: string;
    existingLocation?: string;
  } | null>(null);

  const queryClient = useQueryClient();

  // Cache of loaded sling locations (for displaying selected values)
  const [slingLocationCache, setSlingLocationCache] = useState<Map<string, SlingLocation>>(new Map());

  // State for async dropdown - track loaded options, cursor, and search input
  const [loadedOptions, setLoadedOptions] = useState<SlingLocationOption[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);
  const [currentSearchInput, setCurrentSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_MS = 300;

  // Reset search state when modal closes
  useEffect(() => {
    if (!show) {
      setLoadedOptions([]);
      setCurrentCursor(null);
      setHasMoreOptions(true);
      setCurrentSearchInput('');
    }
  }, [show]);

  // Fetch data
  const { data: locationsData } = useGetCustomersIdLocations(customerId, { query: { enabled: show } });
  const { data: mappingsData, refetch: refetchMappings } = useGetCustomersIdSlingLocationMappings(
    customerId,
    { query: { enabled: show } }
  );

  // Mutations
  const createMapping = usePostSlingLocationMappings();
  const updateMapping = usePutSlingLocationMappingsId();
  const deleteMapping = useDeleteSlingLocationMappingsId();

  // Helper function to generate Sling location key
  const getSlingLocationKey = (srcId: string, locationId: number): string => {
    return `${srcId}:${locationId}`;
  };

  // Detect duplicate Sling location mappings
  const duplicateSlingLocationKeys = useMemo(() => {
    const duplicates = new Set<string>();
    const seen = new Map<string, number>();

    mappingRows.forEach((row) => {
      if (!row.slingLocationSrcId || !row.slingLocationId) return;
      const key = getSlingLocationKey(row.slingLocationSrcId, row.slingLocationId);
      if (seen.has(key)) {
        duplicates.add(key);
      } else {
        seen.set(key, 1);
      }
    });

    return duplicates;
  }, [mappingRows]);

  // Initialize mapping rows when data is loaded
  useEffect(() => {
    if (!locationsData?.data || !mappingsData?.data) {
      return;
    }

    const locations = locationsData.data || [];
    const existingMappings = mappingsData.data || [];
    const mappingMap = new Map<string, CustomerSlingLocationMap>();

    existingMappings.forEach(m => {
      const key = m.location || '';
      mappingMap.set(key, m);
    });

    const rows: MappingRow[] = [];

    // Always add Primary/Default location first
    const primaryMapping = mappingMap.get('');
    rows.push({
      location: null,
      displayName: 'Primary/Default',
      slingLocationSrcId: primaryMapping?.sling_location_src_id || '',
      slingLocationId: primaryMapping?.sling_location_id || 0,
      mappingId: primaryMapping?.id,
      hasChanges: false,
    });

    // Sort other locations alphabetically and add them
    const sortedLocations = [...locations]
      .filter((loc): loc is string => loc !== null && loc !== '')
      .sort();
    sortedLocations.forEach(loc => {
      const mapping = mappingMap.get(loc);
      rows.push({
        location: loc,
        displayName: loc,
        slingLocationSrcId: mapping?.sling_location_src_id || '',
        slingLocationId: mapping?.sling_location_id || 0,
        mappingId: mapping?.id,
        hasChanges: false,
      });
    });

    setMappingRows(rows);

    // Fetch sling locations for existing mappings to populate the cache
    const fetchMappedLocations = async () => {
      // Get initial list of locations (for default display and current selections)
      try {
        const response = await getSlingLocations({ sort_field: 'name' as any, sort_direction: 'asc' });
        if (response.data) {
          const newCache = new Map<string, SlingLocation>();
          response.data.forEach(sl => {
            const key = getSlingLocationKey(sl.src_id || '', sl.id || 0);
            newCache.set(key, sl);
          });
          setSlingLocationCache(newCache);
        }
      } catch (err) {
        console.error('Failed to fetch sling locations:', err);
      }
    };

    fetchMappedLocations();
  }, [locationsData, mappingsData, show]);

  // Convert SlingLocation to option format
  const locationToOption = useCallback((sl: SlingLocation): SlingLocationOption => {
    const key = getSlingLocationKey(sl.src_id || '', sl.id || 0);
    const isAlreadyMapped = !!(sl.mapped_to_customer_id && sl.mapped_to_customer_id !== customerId);
    const label = `${sl.name || 'Unknown'} (${sl.state || 'N/A'})${
      isAlreadyMapped ? ` - Mapped to ${sl.mapped_to_customer_name}` : ''
    }`;
    return {
      value: key,
      label: label,
      isAlreadyMapped: isAlreadyMapped,
      slingLocation: sl,
    };
  }, [customerId]);

  // Fetch locations with optional cursor for pagination
  const fetchLocations = useCallback(async (
    searchInput: string,
    cursor?: string | null,
    append: boolean = false
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (searchInput) params.name = searchInput;
      if (cursor) {
        params.cursor = cursor;
      } else {
        params.sort_field = 'name';
        params.sort_direction = 'asc';
      }

      const response = await getSlingLocations(params);
      const locations = response.data || [];
      const pagination = response.pagination;

      // Update cache with fetched locations
      setSlingLocationCache(prev => {
        const newCache = new Map(prev);
        locations.forEach(sl => {
          const key = getSlingLocationKey(sl.src_id || '', sl.id || 0);
          newCache.set(key, sl);
        });
        return newCache;
      });

      const newOptions = locations.map(locationToOption);

      // Update pagination state
      setCurrentCursor(pagination?.cursor || null);
      setHasMoreOptions(!!pagination?.cursor);

      // Update loaded options
      if (append) {
        setLoadedOptions(prev => [...prev, ...newOptions]);
      } else {
        setLoadedOptions(newOptions);
      }
    } catch (err) {
      console.error('Failed to fetch sling locations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [locationToOption]);

  // Load more options when scrolling to bottom
  const handleMenuScrollToBottom = useCallback(async () => {
    if (!hasMoreOptions || isLoading || !currentCursor) return;
    await fetchLocations(currentSearchInput, currentCursor, true);
  }, [hasMoreOptions, isLoading, currentCursor, currentSearchInput, fetchLocations]);

  // Debounced search - triggers when user types
  const debouncedSearch = useCallback((inputValue: string) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced timer
    debounceTimerRef.current = setTimeout(async () => {
      setCurrentSearchInput(inputValue);
      setCurrentCursor(null);
      setHasMoreOptions(true);
      await fetchLocations(inputValue, null, false);
    }, DEBOUNCE_MS);
  }, [fetchLocations]);

  // Handle input change to trigger debounced search
  const handleInputChange = useCallback((newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      debouncedSearch(newValue);
    }
  }, [debouncedSearch]);

  // Load initial options when menu opens
  const handleMenuOpen = useCallback(() => {
    // Only fetch if we don't have options yet
    if (loadedOptions.length === 0) {
      fetchLocations('', null, false);
    }
  }, [loadedOptions.length, fetchLocations]);

  const handleSlingLocationChange = (rowIndex: number, slingLocationKey: string, slingLocation?: SlingLocation) => {
    if (!slingLocationKey) {
      // Clearing the selection
      setMappingRows(prev => {
        const updated = [...prev];
        updated[rowIndex] = {
          ...updated[rowIndex],
          slingLocationSrcId: '',
          slingLocationId: 0,
          hasChanges: true,
        };
        return updated;
      });
      return;
    }

    // Parse the key: "srcId:locationId"
    const [srcId, locIdStr] = slingLocationKey.split(':');
    const locationId = parseInt(locIdStr, 10);

    // Find the sling location from cache or use the provided one
    const location = slingLocation || slingLocationCache.get(slingLocationKey);

    if (!location) {
      return;
    }

    // First, check if this Sling location is already used in another row in the current UI state
    const existingRowIndex = mappingRows.findIndex((row, idx) =>
      idx !== rowIndex &&
      row.slingLocationSrcId === srcId &&
      row.slingLocationId === locationId
    );

    if (existingRowIndex !== -1) {
      // This Sling location is already mapped to another location in this customer (in current UI state)
      setConfirmMapping({
        rowIndex,
        slingLocation: location,
        existingCustomerName: customerName,
        existingLocation: mappingRows[existingRowIndex].displayName
      });
      return;
    }

    // Then check if this sling location is mapped to a different customer (from database)
    const isAlreadyMapped = location.mapped_to_customer_id &&
      location.mapped_to_customer_id !== customerId;

    if (isAlreadyMapped) {
      // Show confirmation dialog - this will break an existing mapping to another customer
      setConfirmMapping({
        rowIndex,
        slingLocation: location,
        existingCustomerName: location.mapped_to_customer_name || undefined,
        existingLocation: location.mapped_to_location || undefined
      });
      return;
    }

    // Update the row
    setMappingRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        slingLocationSrcId: srcId,
        slingLocationId: locationId,
        hasChanges: true,
      };
      return updated;
    });
  };

  const handleConfirmMappingChange = () => {
    if (!confirmMapping) return;

    const { rowIndex, slingLocation } = confirmMapping;
    const srcId = slingLocation.src_id || '';
    const locationId = slingLocation.id || 0;
    // Get the existing mapping ID from the Sling location data (works globally, not just within current customer)
    const existingMappingId = slingLocation.mapped_to_mapping_id;

    setMappingRows(prev => {
      const updated = [...prev];

      // Clear any existing mapping for this Sling location in the current customer's rows
      for (let i = 0; i < updated.length; i++) {
        if (i !== rowIndex &&
            updated[i].slingLocationSrcId === srcId &&
            updated[i].slingLocationId === locationId) {
          // Clear the old row
          updated[i] = {
            ...updated[i],
            slingLocationSrcId: '',
            slingLocationId: 0,
            mappingId: undefined,
            hasChanges: true,
          };
        }
      }

      // Apply new mapping to current row, using the existing mapping ID if found
      // This will cause an UPDATE instead of DELETE+CREATE
      updated[rowIndex] = {
        ...updated[rowIndex],
        slingLocationSrcId: srcId,
        slingLocationId: locationId,
        mappingId: existingMappingId || updated[rowIndex].mappingId,
        hasChanges: true,
      };

      return updated;
    });
    setConfirmMapping(null);
  };

  const handleSave = async () => {
    setError(null);

    try {
      // Process changes in order: deletions first, then updates, then creates
      // This prevents validation errors when moving a Sling location from one customer location to another

      // Step 1: Process deletions
      for (const row of mappingRows) {
        if (!row.hasChanges) continue;

        if (!row.slingLocationSrcId || !row.slingLocationId) {
          // Delete mapping if it exists
          if (row.mappingId) {
            await deleteMapping.mutateAsync({
              id: row.mappingId,
            });
          }
        }
      }

      // Step 2: Process updates
      for (const row of mappingRows) {
        if (!row.hasChanges) continue;

        if (row.slingLocationSrcId && row.slingLocationId && row.mappingId) {
          // Update existing mapping
          await updateMapping.mutateAsync({
            id: row.mappingId,
            data: {
              customer_id: customerId,
              sling_location_src_id: row.slingLocationSrcId,
              sling_location_id: row.slingLocationId,
              location: row.location || undefined,
            },
          });
        }
      }

      // Step 3: Process creates
      for (const row of mappingRows) {
        if (!row.hasChanges) continue;

        if (row.slingLocationSrcId && row.slingLocationId && !row.mappingId) {
          // Create new mapping
          await createMapping.mutateAsync({
            data: {
              customer_id: customerId,
              sling_location_src_id: row.slingLocationSrcId,
              sling_location_id: row.slingLocationId,
              location: row.location || undefined,
            },
          });
        }
      }

      // Refetch mappings and invalidate cache
      // Invalidate ALL customer-related queries since we might have stolen mappings from other customers
      await refetchMappings();
      // Explicitly invalidate the current customer's detail query to ensure CustomerDetail page updates
      queryClient.invalidateQueries({ queryKey: getGetCustomersIdQueryKey(customerId) });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0] as string;
          // Invalidate all customer mapping queries and customer queries
          return key === 'customers' || key?.includes('customers');
        }
      });
      queryClient.invalidateQueries({ queryKey: getGetSlingLocationsQueryKey() });
      onHide();
    } catch (err: any) {
      // Extract error message from API response
      const errorMessage = err?.response?.data?.error ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Failed to save mappings';
      setError(errorMessage);
    }
  };

  const renderLocationDropdown = (row: MappingRow, rowIndex: number) => {
    const currentKey = row.slingLocationSrcId && row.slingLocationId
      ? getSlingLocationKey(row.slingLocationSrcId, row.slingLocationId)
      : '';

    // Build current value from cache
    let currentValue: SlingLocationOption | null = null;
    if (currentKey) {
      const cachedLocation = slingLocationCache.get(currentKey);
      if (cachedLocation) {
        const isAlreadyMapped = !!(cachedLocation.mapped_to_customer_id && cachedLocation.mapped_to_customer_id !== customerId);
        currentValue = {
          value: currentKey,
          label: `${cachedLocation.name || 'Unknown'} (${cachedLocation.state || 'N/A'})${
            isAlreadyMapped ? ` - Mapped to ${cachedLocation.mapped_to_customer_name}` : ''
          }`,
          isAlreadyMapped: isAlreadyMapped,
          slingLocation: cachedLocation,
        };
      }
    }

    // Check if this selection is a duplicate
    const isDuplicate = currentKey && duplicateSlingLocationKeys.has(currentKey);

    return (
      <Select<SlingLocationOption>
        value={currentValue}
        onChange={(option) => handleSlingLocationChange(rowIndex, option?.value || '', option?.slingLocation)}
        options={loadedOptions}
        isClearable
        isLoading={isLoading}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onMenuOpen={handleMenuOpen}
        filterOption={() => true} // Disable client-side filtering since server does it
        placeholder="Type to search locations..."
        noOptionsMessage={() => isLoading ? 'Loading...' : 'No locations found'}
        loadingMessage={() => 'Searching...'}
        menuPortalTarget={document.body}
        autoSize={false}
        styles={{
          container: (base) => ({
            ...base,
            minWidth: '300px',
          }),
          control: (base, state) => ({
            ...base,
            minHeight: '38px',
            height: '38px',
            fontSize: '0.875rem',
            backgroundColor: 'var(--bs-body-bg)',
            borderColor: isDuplicate ? 'var(--bs-danger)' : (state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)'),
            boxShadow: state.isFocused
              ? (isDuplicate
                  ? '0 0 0 0.25rem rgba(var(--bs-danger-rgb), 0.25)'
                  : '0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25)')
              : undefined,
            '&:hover': {
              borderColor: isDuplicate ? 'var(--bs-danger)' : 'var(--bs-primary)',
            },
          }),
          valueContainer: (base) => ({
            ...base,
            height: '36px',
            padding: '0 8px',
          }),
          input: (base) => ({
            ...base,
            margin: '0px',
            padding: '0px',
            color: 'var(--bs-body-color)',
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: '36px',
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: 'var(--bs-body-bg)',
            borderColor: 'var(--bs-border-color)',
            borderStyle: 'solid',
            borderWidth: '1px',
            zIndex: 9999,
            width: '100%',
            minWidth: '300px',
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: '250px',
          }),
          option: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            color: state.data.isAlreadyMapped ? 'var(--bs-warning-text-emphasis)' : 'var(--bs-body-color)',
            backgroundColor: state.data.isAlreadyMapped && state.isFocused ? 'var(--app-highlight-bg)' :
                            state.data.isAlreadyMapped ? 'var(--bs-warning-bg-subtle)' :
                            state.isFocused ? 'var(--bs-tertiary-bg)' : 'transparent',
          }),
        }}
      />
    );
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Locations - {customerName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </Alert>
          )}

          <Alert variant="info" className="mb-3">
            <strong>Note:</strong> Sling locations can only be mapped to one customer/location combination.
            If you select a location that's already in use, you'll be asked to confirm the change, and if you accept, the old mapping will be removed.
          </Alert>

          {duplicateSlingLocationKeys.size > 0 && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Duplicate Mappings:</strong> The same Sling location cannot be mapped to multiple customer locations.
              Please resolve the highlighted conflicts before saving.
            </Alert>
          )}

          {mappingRows.length === 0 ? (
            <Alert variant="info">
              No locations found for this customer. Events will need to have Program.Location data to map.
            </Alert>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Customer Location</th>
                  <th>Sling Location</th>
                </tr>
              </thead>
              <tbody>
                {mappingRows.map((row, idx) => (
                  <tr key={`${row.location || 'default'}-${idx}`}>
                    <td>
                      <strong>{row.displayName}</strong>
                    </td>
                    <td>{renderLocationDropdown(row, idx)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!mappingRows.some(r => r.hasChanges) || duplicateSlingLocationKeys.size > 0}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation modal for overriding existing mappings */}
      <Modal show={!!confirmMapping} onHide={() => setConfirmMapping(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Mapping Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            The Sling location <strong>{confirmMapping?.slingLocation.name}</strong> is currently mapped to{' '}
            <strong>{confirmMapping?.existingCustomerName || 'Unknown'}</strong>
            {' - '}
            <strong>
              {confirmMapping?.existingLocation || 'Primary/Default'}
            </strong>
            .
            <br /><br />
            Are you sure you want to use this Sling location? This will break the existing mapping.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmMapping(null)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleConfirmMappingChange}>
            Confirm Change
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SlingLocationMappingsModal;
